import { useRouter } from 'next/navigation';
import React from 'react';

import { trackGAEvent } from '@/utility/analytics';
import { HOMETAB_EVENTS } from '@/utility/analyticsConsts';
import { t } from '@/utility/localization';

import { useMagicReachContext } from '@/contexts/MagicReachContext';
import { useWindowWidthContext } from '@/contexts/WindowWidthContext';

import ClampText from '@/components/common/ClampText';
import CopyIconWithTooltip from '@/components/common/CopyIconWithTooltip';
import HeadlessDropdown from '@/components/common/Form/HeadlessDropdown/HeadlessDropdown';
import Icon from '@/components/npl/Icon';
import NPLButton from '@/components/npl/NPLButton';

import InviteMembersBtn from '../../components/InviteMembersBtn';
import { getCreateDropdownMenuOptions } from '../constants';

function HomeTopBar({ activeCommunityLink }) {
  const communityLink = `${window.location.host}${activeCommunityLink}`;
  const router = useRouter();
  const { isGtEqMd } = useWindowWidthContext();
  const { createDraft } = useMagicReachContext();

  const createNewMagicReach = async (link) => {
    await createDraft({});
    router.push(link);
  };

  const handleCreateOptionClick = (option, closeDropdown) => {
    trackGAEvent(option.gaEventId);
    if (option.type === 'magic-reach') {
      createNewMagicReach(option.link);
    } else {
      router.push(option.link);
    }
    closeDropdown();
  };

  const renderDropdownMenuOptions = (closeDropdown) => {
    return (
      <div className="rounded-12 bg-white-default p-8">
        {getCreateDropdownMenuOptions().map((option) => {
          return (
            <div key={option.title}>
              <div className="rounded-12 bg-white-default">
                <button
                  className="flex w-full items-center space-x-12 rounded-8 px-8 py-10 text-label-md text-dark-1b hover:bg-npl-neutral-light-solid-3"
                  onClick={() =>
                    handleCreateOptionClick(option, closeDropdown)
                  }>
                  <Icon name={option.icon} width={16} height={16} />
                  <div className=" text-label-md">{option.title}</div>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mt-32 flex justify-between gap-16">
      <div className="flex flex-col justify-center gap-4 ">
        <div className="hidden text-heading-lg font-semibold md:block">
          {t('home')}
        </div>
        <div className="flex gap-8">
          <ClampText
            text={communityLink}
            line={1}
            hideCTA={true}
            customCTA={() =>
              trackGAEvent(HOMETAB_EVENTS.OPEN_COMMUNITY_LINK)
            }
            linkIsSecondaryColor={true}
            customClassNames="text-label-md !text-dark-1b !text-opacity-65"
            ctaCustomClassNames="!text-npl-text-icon-on-light-surface-secondary/65"
          />
          <CopyIconWithTooltip
            textToCopy={communityLink}
            customCTA={() => {
              trackGAEvent(HOMETAB_EVENTS.COPY_COMMUNITY_LINK);
            }}
            toastText={t('community-link-copied')}
          />
        </div>
      </div>
      <div className="flex flex-row space-x-8 justify-center items-center">
        <InviteMembersBtn
          inviteClickCb={() => {
            trackGAEvent(HOMETAB_EVENTS.INVITE_MEMBER_CLICK);
          }}
          hierarchy="outline"
          btnSize="md"
          btnText={isGtEqMd ? t('invite') : ''}
          rounded={isGtEqMd ? false : true}
          multiply={true}
        />
        <HeadlessDropdown
          placement="bottom-end"
          renderField={() => {
            return (
              <NPLButton
                size="md"
                hierarchy="accent_primary"
                tailIcon="chevron-down"
                buttonText={t('create')}
                onClick={() => {
                  trackGAEvent(HOMETAB_EVENTS.CREATE_BUTTON_CLICK);
                }}
              />
            );
          }}
          renderOptions={({ closeDropdown }) => {
            return renderDropdownMenuOptions(closeDropdown);
          }}
        />
      </div>
    </div>
  );
}

export default HomeTopBar;
